export const META_PIXEL_ID = "941448977055912";

declare global {
	interface Window {
		fbq?: (...args: unknown[]) => void;
	}
}

type MetaEventData = {
	value?: number;
	currency?: string;
	[key: string]: unknown;
};

type MetaEventOptions = {
	eventID?: string;
};

const META_FBC_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const META_FBC_FUTURE_TOLERANCE_MS = 5 * 60 * 1000;
const META_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const META_FBC_STORAGE_KEY = "meta_fbc";
const META_FBP_STORAGE_KEY = "meta_fbp";

function getMetaCookieDomain(): string {
	return /(^|\.)toughcad\.co\.uk$/i.test(window.location.hostname)
		? "; Domain=.toughcad.co.uk"
		: "";
}

function readStorageValue(storage: Storage, key: string): string | null {
	try {
		return storage.getItem(key);
	} catch {
		return null;
	}
}

function updateStorage(operation: () => void): void {
	try {
		operation();
	} catch {
		// Tracking storage is optional and must never block checkout.
	}
}

function getStorageValue(key: string): string | null {
	return (
		readStorageValue(window.sessionStorage, key) ||
		readStorageValue(window.localStorage, key)
	);
}

function setStorageValue(key: string, value: string): void {
	updateStorage(() => window.sessionStorage.setItem(key, value));
	updateStorage(() => window.localStorage.setItem(key, value));
}

function removeStorageValue(key: string): void {
	updateStorage(() => window.sessionStorage.removeItem(key));
	updateStorage(() => window.localStorage.removeItem(key));
}

export function getCookie(name: string): string | null {
	const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
	return match ? match[2] : null;
}

function setCookie(name: string, value: string, maxAgeSeconds: number): void {
	const domain = name === "_fbc" ? getMetaCookieDomain() : "";
	document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${domain}`;
}

function clearCookie(name: string): void {
	document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
	if (name === "_fbc" && getMetaCookieDomain()) {
		document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${getMetaCookieDomain()}`;
	}
}

function isValidMetaFbc(fbc: string): boolean {
	const timestamp = Number(fbc.split(".")[2]);
	const age = Date.now() - timestamp;
	return (
		Number.isFinite(timestamp) &&
		age >= -META_FBC_FUTURE_TOLERANCE_MS &&
		age <= META_FBC_MAX_AGE_MS
	);
}

function getValidMetaFbc(): string | null {
	const cookieValues = document.cookie
		.split(";")
		.map((cookie) => cookie.trim().split("="))
		.filter(([name]) => name === "_fbc")
		.map(([, ...value]) => value.join("="));
	const candidates = [...cookieValues, getStorageValue(META_FBC_STORAGE_KEY)]
		.filter((value): value is string => Boolean(value))
		.filter(isValidMetaFbc);

	return candidates.sort(
		(a, b) => Number(b.split(".")[2]) - Number(a.split(".")[2])
	)[0] || null;
}

function persistMetaFbc(fbc: string): string {
	// Remove a legacy host-only cookie before standardising on the parent domain.
	document.cookie = "_fbc=; path=/; max-age=0; SameSite=Lax";
	setCookie("_fbc", fbc, META_COOKIE_MAX_AGE_SECONDS);
	setStorageValue(META_FBC_STORAGE_KEY, fbc);
	return fbc;
}

function getUrlFbclid(url: string): string | null {
	try {
		return new URL(url, window.location.origin).searchParams.get("fbclid");
	} catch {
		return null;
	}
}

function getMetaFbclid(): string | null {
	return getUrlFbclid(window.location.href);
}

export function formatMetaEventValue(value: unknown): number | null {
	const normalizedValue =
		typeof value === "string" ? value.replace(/,/g, "").trim() : value;
	if (normalizedValue === "") {
		return null;
	}

	const amount = Number(normalizedValue);
	if (!Number.isFinite(amount) || amount < 0) {
		return null;
	}

	return Number(amount.toFixed(2));
}

export function getMetaFbc(): string | null {
	const fbclid = getMetaFbclid();
	if (fbclid) {
		const fbc = `fb.1.${Date.now()}.${fbclid}`;
		return persistMetaFbc(fbc);
	}

	const existingFbc = getValidMetaFbc();
	if (existingFbc) {
		return persistMetaFbc(existingFbc);
	}
	clearCookie("_fbc");
	removeStorageValue(META_FBC_STORAGE_KEY);

	const referrerFbclid = getUrlFbclid(document.referrer);
	if (referrerFbclid) {
		const fbc = `fb.1.${Date.now()}.${referrerFbclid}`;
		return persistMetaFbc(fbc);
	}

	return null;
}

export function getMetaFbp(): string | null {
	const existingFbp = getCookie("_fbp") || getStorageValue(META_FBP_STORAGE_KEY);
	if (existingFbp) return existingFbp;

	const randomId = Math.floor(Math.random() * 10 ** 16);
	const fbp = `fb.1.${Date.now()}.${randomId}`;
	setCookie("_fbp", fbp, META_COOKIE_MAX_AGE_SECONDS);
	setStorageValue(META_FBP_STORAGE_KEY, fbp);
	return fbp;
}

export function generateMetaEventId(): string {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}

	return `${Date.now().toString(36)}-${Math.random()
		.toString(36)
		.slice(2, 12)}`;
}

export function getStoredPurchaseEventId(orderId: string | number): string | null {
	return getStorageValue(`meta_purchase_event_id_${orderId}`);
}

export function storePurchaseEventId(
	orderId: string | number,
	eventId: string
): void {
	setStorageValue(`meta_purchase_event_id_${orderId}`, eventId);
	removeStorageValue(`meta_purchase_pixel_sent_${orderId}`);
}

export function hasSentPurchasePixel(orderId: string | number): boolean {
	return getStorageValue(`meta_purchase_pixel_sent_${orderId}`) === "1";
}

export function markPurchasePixelSent(orderId: string | number): void {
	setStorageValue(`meta_purchase_pixel_sent_${orderId}`, "1");
}

export function trackMetaEvent(
	eventName: string,
	data: MetaEventData = {},
	options?: MetaEventOptions
): void {
	if (!window.fbq) {
		return;
	}

	const eventData = { ...data };
	if (eventData.value != null) {
		const formattedValue = formatMetaEventValue(eventData.value);
		if (formattedValue == null) {
			delete eventData.value;
			delete eventData.currency;
		} else {
			eventData.value = formattedValue;
			eventData.currency = eventData.currency || "GBP";
		}
	}

	window.fbq(
		"track",
		eventName,
		eventData,
		options
	);
}

export function trackMetaPurchase(
	value: unknown,
	eventId: string
): boolean {
	const purchaseValue = formatMetaEventValue(value);
	if (!window.fbq || purchaseValue == null || purchaseValue <= 0) {
		return false;
	}

	trackMetaEvent(
		"Purchase",
		{ value: purchaseValue },
		{ eventID: eventId }
	);
	return true;
}

export function initMetaExternalId(userId: string | number | undefined): void {
	if (!window.fbq || !userId) {
		return;
	}

	window.fbq("init", META_PIXEL_ID, { external_id: String(userId) });
}
